import { useMeeting, usePubSub } from "@videosdk.live/react-sdk";
import React, { useEffect, useRef, useState } from "react";
import { formatAMPM, json_verify, nameTructed } from "../../utils/helper";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";


const ChatMessage = ({ senderId, senderName, text, timestamp }) => {
  const mMeeting = useMeeting();
  const localParticipantId = mMeeting?.localParticipant?.id;
  const localSender = localParticipantId === senderId;

  return (
    <div
      className={`flex ${localSender ? "justify-end" : "justify-start"} mt-4`}
      style={{
        maxWidth: "100%",
      }}
    >
      <div
        className={`flex ${localSender ? "items-end" : "items-start"
          } flex-col py-3 px-4 rounded-lg ${
          localSender 
            ? "bg-violet-600 text-white" 
            : "bg-white border border-violet-200 text-violet-700"
        } shadow-sm max-w-xs`}
      >
        <p className={`text-xs font-medium mb-1 ${
          localSender ? "text-violet-100" : "text-violet-500"
        }`}>
          {localSender ? "You" : nameTructed(senderName, 15)}
        </p>
        <div>
          <p className={`inline-block whitespace-pre-wrap break-words ${
            localSender ? "text-white" : "text-violet-700"
          }`}>
            {text}
          </p>
        </div>
        <div className="mt-2">
          <p className={`text-xs italic ${
            localSender ? "text-violet-200" : "text-violet-400"
          }`}>
            {formatAMPM(new Date(timestamp))}
          </p>
        </div>
      </div>
    </div>
  );
};

const ChatInput = ({ inputHeight }) => {
  const [message, setMessage] = useState("");
  const { publish } = usePubSub("CHAT");
  const input = useRef();

  return (
    <div
      className="w-full flex items-center px-4 py-2 bg-white border-t border-violet-200"
      style={{ height: inputHeight }}
    >
      <div class="relative w-full">
        <span class="absolute inset-y-0 right-0 flex mr-3 items-center">
          <button
            disabled={message.length < 2}
            type="submit"
            className="p-2 focus:outline-none focus:ring-2 focus:ring-violet-300 rounded-md transition-colors duration-150"
            onClick={() => {
              const messageText = message.trim();
              if (messageText.length > 0) {
                try {
                  publish(messageText, { persist: true });
                  setTimeout(() => {
                    setMessage("");
                  }, 100);
                  input.current?.focus();
                } catch (e) {
                  console.log("Error in pubsub", e)
                }
              }
            }}
          >
            <PaperAirplaneIcon
              className={`w-5 h-5 ${message.length < 2 
                ? "text-gray-400" 
                : "text-violet-600 hover:text-violet-700"
              } transition-colors duration-150`}
            />
          </button>
        </span>
        <input
          type="text"
          className="py-3 px-4 text-base text-violet-700 border border-violet-200 bg-white rounded-lg pr-12 pl-4 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-transparent w-full placeholder-violet-400"
          placeholder="Write your message..."
          autocomplete="off"
          ref={input}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              const messageText = message.trim();

              if (messageText.length > 0) {
                try {
                  publish(messageText, { persist: true });
                  setTimeout(() => {
                    setMessage("");
                  }, 100);
                  input.current?.focus();
                } catch (e) {
                  console.log("Error in pubsub", e)
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

const ChatMessages = ({ listHeight }) => {
  const listRef = useRef();
  const { messages } = usePubSub("CHAT");

  const scrollToBottom = (data) => {
    if (!data) {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    } else {
      const { text } = data;

      if (json_verify(text)) {
        const { type } = JSON.parse(text);
        if (type === "CHAT") {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
          }
        }
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return messages ? (
    <div ref={listRef} style={{ overflowY: "scroll", height: listHeight }} className="bg-gradient-to-b from-violet-50 to-white">
      <div className="p-4">
        {messages.map((msg, i) => {
          const { senderId, senderName, message, timestamp } = msg;
          return (
            <ChatMessage
              key={`chat_item_${i}`}
              {...{ senderId, senderName, text: message, timestamp }}
            />
          );
        })}
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-center h-full bg-gradient-to-b from-violet-50 to-white">
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.955 8.955 0 01-2.346-.306c-.584.296-1.233.434-1.951.434a3.5 3.5 0 01-3.5-3.5c0-.718.138-1.367.434-1.951A8.955 8.955 0 014 12a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
          </svg>
        </div>
        <p className="text-violet-500 font-medium">No messages yet</p>
        <p className="text-violet-400 text-sm mt-1">Start a conversation with the participants</p>
      </div>
    </div>
  );
};

export function ChatPanel({ panelHeight }) {
  const inputHeight = 72;
  const listHeight = panelHeight - inputHeight;

  return (
    <div>
      <ChatMessages listHeight={listHeight} />
      <ChatInput inputHeight={inputHeight} />
    </div>
  );
}
